import type { ReactNode } from 'react';
import { Box } from '@mui/material';
import { FEATURES, type FeatureKey } from '@oip/contracts';
import { EmptyState } from '../components/common/EmptyState';
import { LoadingSkeleton } from '../components/common/LoadingSkeleton';
import { useFeatures } from './useFeatures';

/**
 * Blocks a route the tenant is not entitled to.
 *
 * Says "not included in your plan" rather than "not found". A 404 would be a
 * small lie that costs a support ticket: the page exists, it works, and the
 * customer simply has not bought it — which is a sales conversation, not a
 * fault. The distinction is the same one the API draws by returning
 * FEATURE_NOT_ENABLED rather than a bare 403.
 *
 * This is display only. The route being reachable would change nothing, because
 * every endpoint behind it refuses independently.
 */
export function RequireFeature({
  feature,
  children,
}: {
  feature: FeatureKey;
  children: ReactNode;
}) {
  const { features, loading } = useFeatures();

  // Nothing is assumed while loading. Rendering the page and then withdrawing
  // it would read as a fault rather than as a plan boundary.
  if (loading) return <LoadingSkeleton rows={6} />;

  if (!features.includes(feature)) {
    return (
      <Box sx={{ py: 4 }}>
        <EmptyState
          title={`${FEATURES[feature].label} is not included in your plan`}
          message={`${FEATURES[feature].description} Talk to your account manager to enable it — any data already collected is retained.`}
        />
      </Box>
    );
  }
  return <>{children}</>;
}
